/**
 * Shared React components for the proposed-edits pages — `InlineDiffCell`,
 * `AmendForm`, `QuestionsAmendForm`, `QuestionsPreview`, plus the JSX-
 * returning render helpers `renderUrlValue` and `renderDiffCell`.
 *
 * All non-component / non-JSX helpers (types, constants, pure functions)
 * live in `_shared.ts` so this file stays Fast-Refresh-friendly.
 */

import React from 'react'
import axios from 'axios'
import {
  Autocomplete,
  Box,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import type { SxProps, Theme } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'

import QuestionDisplay from '../../components/QuestionDisplay'
import QuestionManager from '../../components/QuestionManager'
import { StructuredQuestion } from '../../interfaces/questions'
import {
  type DiffSegment,
  type ResourceType,
  apiUrl,
  fieldDefFor,
  FIELD_LABELS,
  diffWords,
  formatValue,
  isDiffableString,
  regionIdsFromValue,
  safeHttpUrl,
  normalizeDate,
  questionsFromPayload,
} from './_shared'

interface RegionOption {
  id: number
  name: string
}

/**
 * The full regions taxonomy, loaded once per amend form that actually has a
 * `region_ids` field. Same `paginate: false` fetch the funder/opportunity
 * location managers already use — there are ~3.3k regions and the picker has
 * to be able to offer any of them.
 */
function useRegionOptions(enabled: boolean): {
  options: RegionOption[]
  loading: boolean
  failed: boolean
} {
  const [options, setOptions] = React.useState<RegionOption[]>([])
  const [loading, setLoading] = React.useState(false)
  const [failed, setFailed] = React.useState(false)

  React.useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setFailed(false)
    ;(async () => {
      try {
        const res = await axios.get(`${apiUrl}/regions/`, {
          params: { paginate: false },
        })
        if (cancelled) return
        if (!Array.isArray(res.data)) {
          // A shape change here would otherwise render every chip as
          // "Unknown location (id)" with no hint that the lookup failed.
          throw new Error('regions endpoint did not return an array')
        }
        setOptions(
          res.data.map((r: { id: unknown; name: unknown }) => ({
            id: Number(r.id),
            name: String(r.name),
          })),
        )
      } catch (e) {
        console.error('Failed to load regions for the amend form', e)
        if (!cancelled) setFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [enabled])

  return { options, loading, failed }
}

/**
 * Locations picker for `region_ids` (GH#2060). Renders names, not the raw
 * integer ids the payload carries, and keeps an empty selection expressible —
 * on an opportunity `region_ids: []` means "clear these and inherit the
 * funder's", which is a real proposal shape.
 */
export function RegionsField({
  label,
  value,
  options,
  loading,
  failed,
  resource,
  error,
  onChange,
}: {
  label: string
  value: unknown
  options: RegionOption[]
  loading: boolean
  failed: boolean
  resource: ResourceType
  error?: string
  onChange: (ids: number[]) => void
}): React.ReactElement {
  const byId = React.useMemo(
    () => new Map(options.map((o) => [o.id, o])),
    [options],
  )
  const ids = regionIdsFromValue(value)
  const selected: RegionOption[] = ids.map(
    (id) => byId.get(id) ?? { id, name: `Unknown location (${id})` },
  )
  // `regionIdsFromValue` flattens null to `[]`, but the two mean opposite
  // things on approve: an explicit `[]` clears the joins, a null leaves them
  // alone (`if region_ids is not None` in proposed_edits_admin.py).
  //
  // So a non-array value must not be editable here. Left editable, adding a
  // chip and removing it again would walk null -> [id] -> [], silently turning
  // "leave the locations alone" into "delete every funder_regions row" — data
  // loss through the very affordance this ticket added. The reviewer's
  // no-change options are both still available: leave it, or drop the field
  // with the trash icon (an absent key means the same thing as null).
  //
  // ponytail: inert rather than round-tripping an emptied selection back to
  // null, because no proposal has ever carried this shape (0 of 112 stored
  // region_ids payloads). If null payloads become real and reviewers need to
  // add locations to one, make an emptied selection re-emit null instead.
  const emptyHint =
    resource === 'opportunity'
      ? 'Empty — the opportunity inherits the funder\u2019s locations'
      : 'Empty — this funder would have no locations'

  if (!Array.isArray(value)) {
    // No picker at all — not merely a disabled one. There must be no onChange
    // path that can emit `[]` from a value that means "no change".
    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        disabled
        value="—"
        helperText="No locations set on this proposal — approving leaves the current ones unchanged"
      />
    )
  }

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      size="small"
      loading={loading}
      options={options}
      value={selected}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      getOptionLabel={(o) => o.name}
      onChange={(_e, next) => onChange(next.map((o) => o.id))}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={!!error}
          helperText={
            error ??
            (failed
              ? 'Could not load the locations list — names may show as ids'
              : ids.length === 0
                ? emptyHint
                : `${ids.length} location${ids.length === 1 ? '' : 's'}`)
          }
        />
      )}
    />
  )
}

export function InlineDiffCell({
  segments,
  side,
}: {
  segments: DiffSegment[]
  side: 'before' | 'after'
}) {
  return (
    <>
      {segments.map((seg, idx) => {
        if (seg.type === 'equal') {
          return (
            <span key={idx} style={{ color: '#6b6b6b' }}>
              {seg.text}
            </span>
          )
        }
        if (seg.type === 'remove') {
          if (side !== 'before') return null
          return (
            <span
              key={idx}
              style={{
                color: '#b3261e',
                backgroundColor: '#fde7e9',
                textDecoration: 'line-through',
              }}
            >
              {seg.text}
            </span>
          )
        }
        if (side !== 'after') return null
        return (
          <span
            key={idx}
            style={{ color: '#1b5e20', backgroundColor: '#e6f4ea' }}
          >
            {seg.text}
          </span>
        )
      })}
    </>
  )
}

/**
 * The one boundary between a stored URL value and an `href`.
 *
 * Renders a link only for a value `safeHttpUrl` accepts, and only ever with
 * that canonical value; anything else renders as inert text with no `href` and
 * no click handler. Every review-queue link site goes through here, because the
 * stored corpus predates the ingest gate (GH #1801).
 */
export function SafeExternalLink({
  value,
  sx,
  underline,
  stopPropagation,
}: {
  value: unknown
  sx?: SxProps<Theme>
  underline?: 'none' | 'hover' | 'always'
  stopPropagation?: boolean
}): React.ReactElement {
  const text = typeof value === 'string' ? value : String(value ?? '')
  const href = safeHttpUrl(value)
  if (!href) {
    return (
      <Box component="span" sx={sx}>
        {text}
      </Box>
    )
  }
  return (
    <MuiLink
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      sx={sx}
      underline={underline}
      onClick={stopPropagation ? (e) => e.stopPropagation() : undefined}
    >
      {text}
    </MuiLink>
  )
}

// eslint-disable-next-line react-refresh/only-export-components -- render helper, not a React component
export function renderUrlValue(
  text: unknown,
  variant: 'unchanged' | 'added' | 'removed',
): React.ReactNode {
  const sx =
    variant === 'added'
      ? { color: '#1b5e20', backgroundColor: '#e6f4ea' }
      : variant === 'removed'
        ? {
            color: '#b3261e',
            backgroundColor: '#fde7e9',
            textDecoration: 'line-through',
          }
        : { color: '#6b6b6b' }
  return <SafeExternalLink value={text} sx={sx} stopPropagation />
}

// eslint-disable-next-line react-refresh/only-export-components -- render helper, not a React component
export function renderDiffCell(
  before: unknown,
  after: unknown,
  side: 'before' | 'after',
  changed: boolean,
  formatter?: (v: unknown) => unknown,
  isUrl?: boolean,
): React.ReactNode {
  const applyFormatter = (v: unknown) => (formatter ? formatter(v) : v)
  const formattedBefore = formatValue(applyFormatter(before))
  const formattedAfter = formatValue(applyFormatter(after))
  const raw = side === 'before' ? formattedBefore : formattedAfter
  const sideValue = side === 'before' ? before : after
  if (isUrl && safeHttpUrl(sideValue)) {
    if (!changed) return renderUrlValue(sideValue, 'unchanged')
    return renderUrlValue(sideValue, side === 'before' ? 'removed' : 'added')
  }
  if (!changed) {
    return <span style={{ color: '#6b6b6b' }}>{raw}</span>
  }
  const beforeStr = isDiffableString(applyFormatter(before))
    ? String(applyFormatter(before))
    : formattedBefore
  const afterStr = isDiffableString(applyFormatter(after))
    ? String(applyFormatter(after))
    : formattedAfter
  const bothPresent =
    before !== null && before !== undefined && before !== ''
      ? after !== null && after !== undefined && after !== ''
      : false
  if (!bothPresent) {
    if (side === 'before') {
      if (before === null || before === undefined || before === '') return raw
      return (
        <span
          style={{
            color: '#b3261e',
            backgroundColor: '#fde7e9',
            textDecoration: 'line-through',
          }}
        >
          {raw}
        </span>
      )
    }
    if (after === null || after === undefined || after === '') return raw
    return (
      <span style={{ color: '#1b5e20', backgroundColor: '#e6f4ea' }}>{raw}</span>
    )
  }
  const segments = diffWords(beforeStr, afterStr)
  if (!segments) return raw
  return <InlineDiffCell segments={segments} side={side} />
}

export interface AmendFormProps {
  resource: ResourceType
  value: Record<string, unknown>
  errors: Record<string, string>
  onChange: (value: Record<string, unknown>) => void
}

export const AmendForm: React.FC<AmendFormProps> = ({
  resource,
  value,
  errors,
  onChange,
}) => {
  const keys = Object.keys(value)
  const hasRegions = keys.some(
    (k) => fieldDefFor(resource, k).type === 'regions',
  )
  const regions = useRegionOptions(hasRegions)
  if (keys.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>
        No fields — this proposal would be a no-op. Reject instead.
      </Typography>
    )
  }
  const updateField = (k: string, v: unknown) => {
    onChange({ ...value, [k]: v })
  }
  const removeField = (k: string) => {
    const next = { ...value }
    delete next[k]
    onChange(next)
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {keys.map((k) => {
        const def = fieldDefFor(resource, k)
        const err = errors[k]
        const label = FIELD_LABELS[k] ?? k.replace(/_/g, ' ')
        const readonly = def.type === 'readonly'
        return (
          <Box
            key={k}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              {def.type === 'bool' && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value[k] === true}
                      onChange={(e) => updateField(k, e.target.checked)}
                    />
                  }
                  label={label}
                />
              )}
              {def.type === 'date' && (
                <TextField
                  label={label}
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={normalizeDate(value[k])}
                  onChange={(e) => updateField(k, e.target.value || null)}
                  error={!!err}
                  helperText={err}
                />
              )}
              {(def.type === 'int' || def.type === 'decimal') && (
                <TextField
                  label={label}
                  type="number"
                  size="small"
                  fullWidth
                  inputProps={{
                    step: def.type === 'int' ? 1 : 'any',
                    min: def.min,
                    max: def.max,
                  }}
                  value={
                    value[k] === null || value[k] === undefined
                      ? ''
                      : String(value[k])
                  }
                  onChange={(e) => {
                    const raw = e.target.value
                    if (raw === '') return updateField(k, null)
                    const n = Number(raw)
                    updateField(k, Number.isFinite(n) ? n : raw)
                  }}
                  error={!!err}
                  helperText={err}
                />
              )}
              {(def.type === 'text' ||
                def.type === 'textarea' ||
                def.type === 'url' ||
                def.type === 'email') && (
                <TextField
                  label={label}
                  size="small"
                  fullWidth
                  multiline={def.type === 'textarea'}
                  minRows={def.type === 'textarea' ? 3 : undefined}
                  type={def.type === 'url' ? 'url' : undefined}
                  inputProps={{ maxLength: def.maxLength }}
                  value={
                    value[k] === null || value[k] === undefined
                      ? ''
                      : String(value[k])
                  }
                  onChange={(e) =>
                    updateField(
                      k,
                      // An emptied email must reach the backend as null:
                      // pydantic EmailStr rejects '', but an explicit null
                      // clears the column (GH#2060).
                      def.type === 'email' && e.target.value === ''
                        ? null
                        : e.target.value,
                    )
                  }
                  error={!!err}
                  helperText={
                    err ??
                    (def.maxLength
                      ? `${String(value[k] ?? '').length}/${def.maxLength}`
                      : undefined)
                  }
                />
              )}
              {def.type === 'select' && (
                <FormControl fullWidth size="small" error={!!err}>
                  <InputLabel id={`amend-${k}-label`}>{label}</InputLabel>
                  <Select
                    labelId={`amend-${k}-label`}
                    label={label}
                    value={
                      typeof value[k] === 'string' ? (value[k] as string) : ''
                    }
                    onChange={(e) =>
                      updateField(k, e.target.value === '' ? null : e.target.value)
                    }
                  >
                    {(def.options ?? []).map((opt) => (
                      <MenuItem key={opt || '__empty__'} value={opt}>
                        {opt || '(Not set)'}
                      </MenuItem>
                    ))}
                  </Select>
                  {err && <FormHelperText>{err}</FormHelperText>}
                </FormControl>
              )}
              {def.type === 'regions' && (
                <RegionsField
                  label={label}
                  value={value[k]}
                  options={regions.options}
                  loading={regions.loading}
                  failed={regions.failed}
                  resource={resource}
                  error={err}
                  onChange={(ids) => updateField(k, ids)}
                />
              )}
              {def.type === 'readonly' && (
                <TextField
                  label={`${label} (not editable)`}
                  size="small"
                  fullWidth
                  disabled
                  value={formatValue(value[k])}
                />
              )}
            </Box>
            <Tooltip title="Drop this field from the proposal">
              <IconButton
                size="small"
                onClick={() => removeField(k)}
                disabled={readonly}
                sx={{ mt: def.type === 'bool' ? 0.5 : 1 }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        )
      })}
    </Box>
  )
}

export interface QuestionsAmendFormProps {
  value: Record<string, unknown>
  onChange: (value: Record<string, unknown>) => void
  currentRecord?: Record<string, unknown> | null
  applicationLink?: string | null
}

export const QuestionsAmendForm: React.FC<QuestionsAmendFormProps> = ({
  value,
  onChange,
  currentRecord,
  applicationLink,
}) => {
  const questions = questionsFromPayload(value)
  const rawQuestions = Array.isArray(currentRecord?.questions)
    ? (currentRecord?.questions as string[])
    : []

  return (
    <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
      <QuestionManager
        rawQuestions={rawQuestions}
        structuredQuestions={questions}
        onQuestionsChange={(next) => onChange({ ...value, questions: next })}
        applicationLink={applicationLink ?? undefined}
      />
    </Box>
  )
}

export const QuestionsPreview: React.FC<{ questions: StructuredQuestion[] }> = ({
  questions,
}) => {
  if (questions.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No structured questions.
      </Typography>
    )
  }
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {questions.map((question, index) => (
        <Paper
          key={`${question.question_number}-${index}`}
          variant="outlined"
          sx={{ p: 1.5 }}
        >
          <QuestionDisplay question={question} />
        </Paper>
      ))}
    </Box>
  )
}
